import { useState } from 'react';
import { supabase } from '../../api';
import { validateEmail, validatePhone } from '../../utils/validation';

interface ValidationOptions {
  userId: string;
  currentUsername?: string;
  currentEmail?: string;
  currentPhone?: string;
}

export default function useValidateUserFields(options: ValidationOptions) {
  const { userId, currentUsername, currentEmail, currentPhone } = options;
  const [isUsernameExists, setIsUsernameExists] = useState<boolean>(false);
  const [isEmailExists, setIsEmailExists] = useState<boolean>(false);
  const [isPhoneExists, setIsPhoneExists] = useState<boolean>(false);
  const [isEmailFormatValid, setIsEmailFormatValid] = useState<boolean>(true);
  const [isPhoneFormatValid, setIsPhoneFormatValid] = useState<boolean>(true);
  const [isValidating, setIsValidating] = useState<boolean>(false);

  const checkUsernameExists = async (username: string) => {
    if (!username || username === currentUsername) {
      setIsUsernameExists(false);
      return false;
    }

    setIsValidating(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('username', username)
        .neq('id', userId)
        .maybeSingle();

      const exists = !!data;
      setIsUsernameExists(exists);
      return exists;
    } catch (error) {
      console.error('Error checking username:', error);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const checkEmailExists = async (email: string) => {
    const isValid = validateEmailFormat(email);

    if (!isValid) {
      return false;
    }

    if (!email || email === currentEmail) {
      setIsEmailExists(false);
      return false;
    }

    setIsValidating(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('email', email)
        .neq('id', userId)
        .maybeSingle();

      const exists = !!data;
      setIsEmailExists(exists);
      return exists;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const checkPhoneExists = async (phone: string) => {
    if (!phone || phone === currentPhone) {
      setIsPhoneExists(false);
      return false;
    }

    const cleanedPhone = phone.replace(/\D/g, '');

    setIsValidating(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('phone', cleanedPhone)
        .neq('id', userId)
        .maybeSingle();

      const exists = !!data;
      setIsPhoneExists(exists);
      return exists;
    } catch (error) {
      console.error('Error checking phone:', error);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const validateEmailFormat = (email: string) => {
    if (!email) {
      setIsEmailFormatValid(true);
      return true;
    }

    const isValid = !!validateEmail(email);
    setIsEmailFormatValid(isValid);
    return isValid;
  };

  const validatePhoneFormat = (phone: string) => {
    if (!phone) {
      setIsPhoneFormatValid(true);
      return true;
    }

    const isValid = validatePhone(phone);
    setIsPhoneFormatValid(isValid);
    return isValid;
  };

  return {
    isUsernameExists,
    isEmailExists,
    isPhoneExists,
    isEmailFormatValid,
    isPhoneFormatValid,
    isValidating,
    checkUsernameExists,
    checkEmailExists,
    checkPhoneExists,
    validateEmailFormat,
    validatePhoneFormat,
  };
}
